import { FormikHelpers, FormikProps, useFormik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CreateReportFormValues, CreateReportOptions } from 'src/components/star-grid/createReport.types';
import {
  CREATE_REPORT_DEFAULT_MEASURE,
  CREATE_REPORT_DEFAULT_SOURCE,
  CREATE_REPORT_SOURCE_OPTIONS
} from 'src/components/star-grid/createReportConstants';
import { useAsyncResource, isResourcePending } from 'src/components/star-grid/hooks/useAsyncResource';
import { DataSyncText, useDataSyncText } from 'src/components/star-grid/hooks/useDataSyncText';
import { createPnlReport, getPnlConfig, listPnlHierarchies } from 'src/components/star-grid/services/starGridService';
import { PNL_LINE_ITEM_HIERARCHY_ID } from 'src/components/star-grid/starGridConstants';
import { PnlSourceMode } from 'src/components/star-grid/starGridTypes';
import {
  createReportFormSchema,
  hierarchyDimensionItems,
  initialCreateReportValues,
  reportColumnsError,
  reportFieldError
} from 'src/components/star-grid/utils/createReportForm';
import { createReportMeasure, createReportRequest, reportVarianceOptions } from 'src/components/star-grid/utils/createReportRequest';

const useReportSubmission = (onCreated: (id: number) => void, options: CreateReportOptions, text: DataSyncText) => {
  const [submitError, setSubmitError] = useState<string>();
  const onSubmit = async (values: CreateReportFormValues, helpers: FormikHelpers<CreateReportFormValues>): Promise<void> => {
    setSubmitError(undefined);
    try {
      const created = await createPnlReport(createReportRequest(values, options));
      onCreated(created.pnl_report_id);
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : text('createFailed'));
    } finally {
      helpers.setSubmitting(false);
    }
  };
  return { submitError, onSubmit };
};

const useReportFormFields = (formik: FormikProps<CreateReportFormValues>, text: DataSyncText) => {
  const { values, errors, touched, submitCount, setFieldValue, setFieldTouched } = formik;
  const setValue = <K extends keyof CreateReportFormValues>(field: K, next: CreateReportFormValues[K]): void => {
    void setFieldValue(field, next, true);
    void setFieldTouched(field, true, false);
  };
  const varianceAvailableScenarios = useMemo(() => reportVarianceOptions(values), [values.scenario_selections]);
  // Per-chip leaf selections, keyed by chip identity (hierarchyId) — the value each chip's
  // embedded dropdown emits.
  const setRowValue = (id: string, next: string[]): void => setValue('row_values', { ...values.row_values, [id]: next });
  const setFilterValue = (id: string, next: string[]): void => setValue('filter_values', { ...values.filter_values, [id]: next });
  return {
    values,
    setValue,
    setFieldTouched,
    setRowValue,
    setFilterValue,
    varianceAvailableScenarios,
    nameError: reportFieldError(errors.report_name, Boolean(touched.report_name) || submitCount > 0),
    dimensionsError: reportFieldError(errors.selected_dimensions, Boolean(touched.selected_dimensions) || submitCount > 0),
    scenarioSelectionsError: reportColumnsError(errors.scenario_selections, Boolean(touched.scenario_selections) || submitCount > 0, text),
    hasValidationErrors: submitCount > 0 && Object.keys(errors).length > 0,
    isSubmitting: formik.isSubmitting
  };
};

export const useCreatePnlReportForm = (onCreated: (id: number) => void) => {
  const text = useDataSyncText();
  const resource = useAsyncResource(getPnlConfig);
  useEffect(() => resource.ensureLoaded(), [resource.ensureLoaded]);
  const [measure, setMeasure] = useState<string>(CREATE_REPORT_DEFAULT_MEASURE);
  const [sourceMode, setSourceMode] = useState<PnlSourceMode>(CREATE_REPORT_DEFAULT_SOURCE);
  const submission = useReportSubmission(onCreated, { measure, sourceMode }, text);
  const formik = useFormik<CreateReportFormValues>({
    initialValues: initialCreateReportValues(text),
    validationSchema: createReportFormSchema(text),
    onSubmit: submission.onSubmit
  });
  const fields = useReportFormFields(formik, text);

  // Dimensions come from the ROLLUP HIERARCHIES (dimension_registry.json ∩ pnl_paths), not the
  // flat available_dimensions list — one draggable chip per hierarchy, each carrying its
  // hierarchyId so the chip can show its value tree.
  const hierarchies = useAsyncResource(listPnlHierarchies);
  useEffect(() => hierarchies.ensureLoaded(), [hierarchies.ensureLoaded]);
  const allDimensions = useMemo(() => hierarchyDimensionItems(hierarchies.data), [hierarchies.data]);

  // Seed a default row once the (async) registry lands, so the form opens with a usable row
  // instead of failing validation. Once only — re-seeding would fight a user who removed it.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || allDimensions.length === 0) return;
    seeded.current = true;
    if (formik.values.selected_dimensions.length === 0) {
      const preferred = allDimensions.find((d) => d.hierarchyId === PNL_LINE_ITEM_HIERARCHY_ID) ?? allDimensions[0];
      void formik.setFieldValue('selected_dimensions', [preferred], true);
    }
  }, [allDimensions, formik]);
  const sourceModeOptions = CREATE_REPORT_SOURCE_OPTIONS.map((option) => ({
    value: option.value,
    label: text(option.labelKey),
    description: text(option.descriptionKey)
  }));
  const measureOptions = [...new Set((resource.data?.available_measures ?? [CREATE_REPORT_DEFAULT_MEASURE]).map(createReportMeasure))].map(
    (value) => ({ label: value, value })
  );
  return {
    ...fields,
    text,
    formik,
    allDimensions,
    sourceModeOptions,
    measureOptions,
    measure,
    setMeasure,
    sourceMode,
    setSourceMode,
    config: resource.data,
    configLoading: isResourcePending(resource),
    configError: resource.error,
    hierarchiesLoading: isResourcePending(hierarchies),
    hierarchiesError: hierarchies.error,
    submitError: submission.submitError
  };
};
